import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  connectionStringsApi,
  jobTogglesApi,
  lookupApi,
  tenantSetupApi,
  tenantsApi,
  whiteListedIpApi,
} from '@/api/services/tenants';
import type {
  ResetAdminPasswordDto,
  TenantClientInfoDto,
  TenantContactsDetailsDto,
  TenantCreateDto,
  TenantReportInformationUpdateDto,
  TenantRtExperienceDto,
  TenantUpdateDto,
} from '@/api/types/tenants';

export const tenantKeys = {
  all: ['tenants'] as const,
  list: ['tenants', 'list'] as const,
  detail: (id: string) => ['tenants', 'detail', id] as const,
  connectionString: (id: string) => ['tenants', 'connectionString', id] as const,
  info: (id: string) => ['tenants', 'info', id] as const,
  setting: (id: string) => ['tenants', 'setting', id] as const,
  reports: (id: string) => ['tenants', 'reports', id] as const,
  details: (id: string) => ['tenants', 'details', id] as const,
  rtExperience: (id: string) => ['tenants', 'rtExperience', id] as const,
  connectionStrings: (id: string) => ['tenants', 'connectionStrings', id] as const,
  whiteListedIps: (id: string) => ['tenants', 'whiteListedIps', id] as const,
  jobToggles: (id: string) => ['tenants', 'jobToggles', id] as const,
  countries: (id: string) => ['tenants', 'lookup', 'countries', id] as const,
  currencies: (id: string) => ['tenants', 'lookup', 'currencies', id] as const,
};

export function useTenant(id: string | undefined) {
  return useQuery({
    queryKey: tenantKeys.detail(id ?? ''),
    queryFn: () => tenantsApi.get(id as string),
    enabled: !!id,
  });
}

export function useCreateTenant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: TenantCreateDto) => tenantsApi.create(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: tenantKeys.list }),
  });
}

export function useUpdateTenant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: TenantUpdateDto }) =>
      tenantsApi.update(id, input),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: tenantKeys.list });
      qc.invalidateQueries({ queryKey: tenantKeys.detail(vars.id) });
    },
  });
}

export function useDeleteTenant() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => tenantsApi.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: tenantKeys.list }),
  });
}

export function useResetAdminPassword() {
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: ResetAdminPasswordDto }) =>
      tenantsApi.resetAdminPassword(id, input),
  });
}

export function useDefaultConnectionString(id: string | undefined) {
  return useQuery({
    queryKey: tenantKeys.connectionString(id ?? ''),
    queryFn: () => tenantsApi.getDefaultConnectionString(id as string),
    enabled: !!id,
  });
}

export function useUpdateDefaultConnectionString() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, value }: { id: string; value: string }) =>
      tenantsApi.updateDefaultConnectionString(id, value),
    onSuccess: (_data, vars) =>
      qc.invalidateQueries({ queryKey: tenantKeys.connectionString(vars.id) }),
  });
}

export function useDeleteDefaultConnectionString() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => tenantsApi.deleteDefaultConnectionString(id),
    onSuccess: (_data, id) =>
      qc.invalidateQueries({ queryKey: tenantKeys.connectionString(id) }),
  });
}

// ---- Edit tabs: client info ----
export function useTenantInfo(id: string) {
  return useQuery({ queryKey: tenantKeys.info(id), queryFn: () => tenantSetupApi.getInfo(id) });
}
export function useTenantSetting(id: string) {
  return useQuery({ queryKey: tenantKeys.setting(id), queryFn: () => tenantSetupApi.getSetting(id) });
}
export function useUpdateClientInfo(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: TenantClientInfoDto) => tenantSetupApi.updateClientInfo(id, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: tenantKeys.info(id) });
      qc.invalidateQueries({ queryKey: tenantKeys.setting(id) });
    },
  });
}

// ---- Edit tabs: reports ----
export function useTenantReports(id: string) {
  return useQuery({ queryKey: tenantKeys.reports(id), queryFn: () => tenantSetupApi.getReports(id) });
}
export function useUpdateReports(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: TenantReportInformationUpdateDto) => tenantSetupApi.updateReports(id, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: tenantKeys.reports(id) }),
  });
}

// ---- Edit tabs: contact details ----
export function useTenantDetails(id: string) {
  return useQuery({ queryKey: tenantKeys.details(id), queryFn: () => tenantSetupApi.getDetails(id) });
}
export function useUpdateContactDetails(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: TenantContactsDetailsDto) => tenantSetupApi.updateContactDetails(id, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: tenantKeys.details(id) }),
  });
}

// ---- Edit tabs: RT experience ----
export function useTenantRtExperience(id: string) {
  return useQuery({
    queryKey: tenantKeys.rtExperience(id),
    queryFn: () => tenantSetupApi.getRtExperience(id),
  });
}
export function useUpdateRtExperience(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: TenantRtExperienceDto) => tenantSetupApi.updateRtExperience(id, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: tenantKeys.rtExperience(id) }),
  });
}

// ---- Edit tabs: connection strings ----
export function useConnectionStrings(id: string) {
  return useQuery({
    queryKey: tenantKeys.connectionStrings(id),
    queryFn: () => connectionStringsApi.getAll(id),
  });
}
export function useCreateConnectionString(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { name: string; connectionString: string }) =>
      connectionStringsApi.create({ tenantId: id, ...input }),
    onSuccess: () => qc.invalidateQueries({ queryKey: tenantKeys.connectionStrings(id) }),
  });
}
export function useUpdateConnectionString(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { name: string; connectionString: string }) =>
      connectionStringsApi.update({ tenantId: id, ...input }),
    onSuccess: () => qc.invalidateQueries({ queryKey: tenantKeys.connectionStrings(id) }),
  });
}
export function useDeleteConnectionString(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (name: string) => connectionStringsApi.remove(id, name),
    onSuccess: () => qc.invalidateQueries({ queryKey: tenantKeys.connectionStrings(id) }),
  });
}

// ---- Edit tabs: white-listed IPs ----
export function useWhiteListedIps(id: string) {
  return useQuery({
    queryKey: tenantKeys.whiteListedIps(id),
    queryFn: () => whiteListedIpApi.getList(id),
  });
}
export function useCreateWhiteListedIp(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ip: string) => whiteListedIpApi.create({ tenantId: id, ip }),
    onSuccess: () => qc.invalidateQueries({ queryKey: tenantKeys.whiteListedIps(id) }),
  });
}
export function useUpdateWhiteListedIp(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: number; ip: string }) =>
      whiteListedIpApi.update({ tenantId: id, ...input }),
    onSuccess: () => qc.invalidateQueries({ queryKey: tenantKeys.whiteListedIps(id) }),
  });
}
export function useDeleteWhiteListedIp(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ipId: number) => whiteListedIpApi.remove(ipId, id),
    onSuccess: () => qc.invalidateQueries({ queryKey: tenantKeys.whiteListedIps(id) }),
  });
}

// ---- Edit tabs: job toggles ----
export function useJobToggles(id: string) {
  return useQuery({ queryKey: tenantKeys.jobToggles(id), queryFn: () => jobTogglesApi.get(id) });
}
export function useSaveJobToggles(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (states: Record<string, boolean>) => jobTogglesApi.save(id, states),
    onSuccess: () => qc.invalidateQueries({ queryKey: tenantKeys.jobToggles(id) }),
  });
}

// ---- Lookups ----
export function useCountries(id: string) {
  return useQuery({
    queryKey: tenantKeys.countries(id),
    queryFn: () => lookupApi.getCountries(id),
    staleTime: 5 * 60 * 1000,
  });
}
export function useCurrenciesWithSymbols(id: string) {
  return useQuery({
    queryKey: tenantKeys.currencies(id),
    queryFn: () => lookupApi.getCurrenciesWithSymbols(id),
    staleTime: 5 * 60 * 1000,
  });
}
